import {
  Accordion,
  AccordionContent,
  AccordionTitle,
  PageLoader,
  Banner,
  Box,
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateTitle,
  Flex,
  FormattedRelativeTime,
  Icon,
  InlineStack,
  Label,
  PlainButton,
  Scrollable,
  Stack,
  Text,
  Toggle,
  Truncated,
} from '@customerio/pluma-components/react';
import { PageTitle } from '@/components/page-title';
import { useNavigateToAgentChat } from '@/features/copilot';
import { useAnalytics } from '@/services/analytics';
import { GradientRing, gradientHoverTarget } from '@/components/feedback/gradient-ring';
import { AgentLayout } from '../layout';
import { useRoutines } from './hooks/use-routines';
import { formatSchedule, getRoutineStatus } from './utils';
import { RoutineDetail } from './components/routine-detail';
import { useCustomerFeedback } from '@/features/customer-feedback';
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import clsx from 'clsx';
import * as styles from './index.css';
import type { ServicesApiCopilotCron } from '@/services/api/services-api';

const CREATE_ROUTINE_MESSAGE =
  "I'd like to create a new routine. Ask me what I want to keep track of, how often I'd like updates, and what I should know in each summary.";

interface RoutineSuggestion {
  title: string;
  description: string;
  message: string;
}

const ROUTINE_SUGGESTIONS: RoutineSuggestion[] = [
  {
    title: 'Deliverability watchdog',
    description: 'Monitor bounce rates, spam complaints, and domain reputation trends.',
    message:
      "Create a routine that monitors my email deliverability health. Track bounce rates, spam complaint rates, suppression list growth, and domain reputation signals. If anything is trending in the wrong direction — even gradually — flag it immediately and explain what's likely causing it, how severe it is, and what concrete steps I should take to fix it before it impacts my sending reputation.",
  },
  {
    title: 'Broadcast recap',
    description: 'Compare how recent broadcasts performed and surface actionable takeaways.',
    message:
      'Create a routine that summarizes how my recent broadcasts performed. For each one, pull open rates, click rates, unsubscribe rates, and any revenue attributed. Compare them against each other so I can see which subject lines, send times, or content approaches are actually working. Call out the top performers and the underperformers, and give me specific, actionable takeaways I can apply to my next broadcast.',
  },
  {
    title: 'Segment hygiene check',
    description: 'Audit segments for unused, empty, or redundant conditions.',
    message:
      "Create a routine that audits all of my segments and flags anything that needs cleanup. I want to know about unused segments that aren't referenced by any active campaign or workflow, segments with zero or near-zero members, segments with overlapping or redundant conditions, and segments that haven't been touched or referenced in over 90 days. For each issue you find, recommend whether I should update, merge, or delete it.",
  },
  {
    title: 'Goal conversion report',
    description: 'Track conversion rates and week-over-week trends across all goals.',
    message:
      'Create a routine that pulls conversion data across all of my goals. For each goal, include the conversion rate, week-over-week trend, and which campaigns or workflows are driving the most attributed conversions and revenue. Highlight anything that improved or declined significantly so I know where to focus my attention for the week ahead.',
  },
  {
    title: 'Subscriber churn early warning',
    description: 'Spot at-risk subscribers before they disengage or unsubscribe.',
    message:
      "Create a routine that scans my active subscriber base for early warning signs of churn. Look for patterns like declining email engagement over the last 30–60 days, inactivity streaks, or customers who match the behavioral profile of people who've previously unsubscribed or gone dormant. Flag the at-risk subscribers and recommend which existing workflows or campaigns I should use to re-engage them — or suggest a retention campaign if one doesn't exist yet.",
  },
  {
    title: 'Audience growth tracker',
    description: 'Track how key segments are growing or shrinking over time.',
    message:
      'Create a routine that tracks the size of my key segments over time by logging their membership counts. Over weeks, build a trend line for each one so I can see whether my high-value segments are growing, whether my "lapsed" or "inactive" segments are ballooning, and where the overall momentum of my audience is heading. Surface any notable shifts — positive or negative — so I can connect them back to recent campaigns or product changes.',
  },
];

/**
 * Picks `count` random items from `ROUTINE_SUGGESTIONS` using a stable
 * selection per component mount via useState's lazy initializer.
 */
function useRandomSuggestions(count: number): RoutineSuggestion[] {
  const [suggestions] = useState(() => {
    const shuffled = [...ROUTINE_SUGGESTIONS];
    for (let index = shuffled.length - 1; index > 0; index--) {
      const index_ = Math.floor(Math.random() * (index + 1));
      [shuffled[index], shuffled[index_]] = [shuffled[index_], shuffled[index]];
    }
    return shuffled.slice(0, count);
  });
  return suggestions;
}

function SuggestionCard({
  icon,
  title,
  description,
  onClick,
}: {
  icon: 'sparkle' | 'add';
  title: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <GradientRing radius={4} display="flex" className={styles.suggestionCardInline}>
      <PlainButton className={clsx(styles.suggestionCard, gradientHoverTarget)} onClick={onClick}>
        <Flex alignItems="center" gap="050">
          <Icon name={icon} size="xs" color="subtle" />
          <div className={styles.suggestionCardTitle}>{title}</div>
        </Flex>
        <div className={styles.suggestionCardDescription}>{description}</div>
      </PlainButton>
    </GradientRing>
  );
}

function SuggestionCards({
  suggestions,
  navigateToChat,
  track,
}: {
  suggestions: RoutineSuggestion[];
  navigateToChat: ReturnType<typeof useNavigateToAgentChat>;
  track: ReturnType<typeof useAnalytics>['track'];
}) {
  return (
    <Scrollable overflow="x" withEdgeIndicators={true}>
      <Flex gap="200" alignItems="stretch">
        {suggestions.map((suggestion) => (
          <SuggestionCard
            key={suggestion.title}
            icon="sparkle"
            title={suggestion.title}
            description={suggestion.description}
            onClick={() => {
              track('aiAssistant', 'routineSuggestionClicked', { Title: suggestion.title });
              navigateToChat({ message: suggestion.message });
            }}
          />
        ))}
        <SuggestionCard
          icon="add"
          title="Create a custom routine"
          description="Describe what you want to track and how often you'd like updates."
          onClick={() => {
            track('aiAssistant', 'routineSuggestionClicked', { Title: 'Create a custom routine' });
            navigateToChat({ message: CREATE_ROUTINE_MESSAGE });
          }}
        />
      </Flex>
    </Scrollable>
  );
}

function RoutineCard({
  routine,
  workspaceId,
  toggleRoutine,
}: {
  routine: ServicesApiCopilotCron;
  workspaceId: number;
  toggleRoutine: (routineId: number) => Promise<void>;
}) {
  const toggleMutation = useMutation({ mutationFn: () => toggleRoutine(routine.id) });
  const schedule = formatSchedule(routine.schedule);
  const { color: statusColor, label: statusLabel } = getRoutineStatus(routine);

  return (
    <Accordion variant="bordered" w="full">
      <AccordionTitle>
        <div className={styles.routineTitleContent}>
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- stop click from toggling accordion */}
          <div onClick={(event) => event.stopPropagation()}>
            <Toggle
              size="sm"
              ariaLabel={routine.paused ? 'Resume routine' : 'Pause routine'}
              isChecked={!routine.paused}
              isDisabled={toggleMutation.isPending}
              onChange={() => toggleMutation.mutate()}
            />
          </div>
          <Stack gap="050" className={styles.routineName}>
            <InlineStack gap="100" alignItems="center" minW="0">
              <Text size="s" weight="semibold">
                <Truncated>{routine.name}</Truncated>
              </Text>
              <Label showStatusIndicator={true} color={statusColor}>
                {statusLabel}
              </Label>
            </InlineStack>
            <InlineStack gap="200">
              {!routine.paused ? (
                <Text size="xs" color="subtle">
                  {schedule}
                </Text>
              ) : null}
              {!routine.paused && routine.next_run_at != null ? (
                <Text size="xs" color="subtle">
                  Next:{' '}
                  <FormattedRelativeTime date={routine.next_run_at} withTooltip={true} shouldUppercaseFirst={false} />
                </Text>
              ) : null}
              {routine.last_run_at != null ? (
                <Text size="xs" color="subtle">
                  Last:{' '}
                  <FormattedRelativeTime date={routine.last_run_at} withTooltip={true} shouldUppercaseFirst={false} />
                </Text>
              ) : (
                <Text size="xs" color="subtle">
                  Never run
                </Text>
              )}
            </InlineStack>
          </Stack>
        </div>
      </AccordionTitle>
      <AccordionContent>
        <RoutineDetail routine={routine} workspaceId={workspaceId} />
      </AccordionContent>
    </Accordion>
  );
}

function RoutinesContent() {
  const { routines, isLoading, isError, toggleRoutine, workspaceId, maxActive } = useRoutines();
  const navigateToChat = useNavigateToAgentChat();
  const { track } = useAnalytics();
  const showFeedbackModal = useCustomerFeedback();
  const suggestions = useRandomSuggestions(2);

  if (isLoading) {
    return <PageLoader label="Loading routines" />;
  }

  if (isError) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" flexGrow="1">
        <EmptyState>
          <EmptyStateTitle icon="error">Unable to load routines</EmptyStateTitle>
          <EmptyStateDescription>Something went wrong. Please try again later.</EmptyStateDescription>
        </EmptyState>
      </Box>
    );
  }

  if (routines.length === 0) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" p="300">
        <EmptyState className={styles.fullWidthEmptyState}>
          <EmptyStateTitle icon="trigger-date">Set up a routine</EmptyStateTitle>
          <EmptyStateDescription>
            Your routine runs on a schedule and sends you a summary by email.
          </EmptyStateDescription>
          <EmptyStateActions w="full">
            <SuggestionCards suggestions={suggestions} navigateToChat={navigateToChat} track={track} />
          </EmptyStateActions>
        </EmptyState>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap="300" w="full" p="300">
      <Box display="flex" flexDirection="column" gap="200">
        <Text size="s" weight="semibold" color="subtle">
          Add another routine
        </Text>
        <SuggestionCards suggestions={suggestions} navigateToChat={navigateToChat} track={track} />
      </Box>

      <Box display="flex" flexDirection="column" gap="200">
        <Text size="s" weight="semibold" color="subtle">
          Your routines
        </Text>
        {Number.isFinite(maxActive) ? (
          <Banner
            variant="information"
            icon="help"
            title={
              maxActive === 1
                ? 'One routine can be active at a time'
                : `Up to ${String(maxActive)} routines can be active at a time`
            }
            action={{
              label: 'Give feedback',
              onClick: () => {
                void showFeedbackModal({ feature: 'Routines' });
              },
            }}
          >
            We are testing out Routines. If you see value in this feature let us know.
          </Banner>
        ) : null}
        {routines.map((routine) => (
          <RoutineCard key={routine.id} routine={routine} workspaceId={workspaceId} toggleRoutine={toggleRoutine} />
        ))}
      </Box>
    </Box>
  );
}

export function RoutinesPage() {
  return (
    <AgentLayout title="Routines">
      <PageTitle title="Routines" />
      <Box overflow="auto" flexGrow="1">
        <RoutinesContent />
      </Box>
    </AgentLayout>
  );
}
